import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ArrowUp, ArrowDown, Maximize2, Minimize2, X, Filter as FilterIcon, Check } from "lucide-react";

/**
 * Excel-like DataTable
 * Props:
 *   columns: [{
 *     key: string,
 *     label: string,
 *     width?: string | number,            // e.g. "180px" or 180
 *     sortable?: boolean,
 *     filterable?: boolean,               // shows a per-column filter input
 *     editable?: boolean,                 // double-click to edit; saved via onCellEdit
 *     align?: "left"|"right"|"center",
 *     accessor?: (row) => any,            // value for sort/filter/render fallback
 *     render?: (row, value) => ReactNode, // custom cell renderer
 *     editor?: ({ value, onChange, onCommit, onCancel }) => ReactNode, // inline editor
 *   }]
 *   rows: object[]
 *   rowKey: (row) => string
 *   onRowClick?: (row) => void
 *   onCellEdit?: ({ row, column, value }) => Promise<void> | void
 *   actions?: (row) => ReactNode         // right-most "actions" column
 *   storageKey?: string                   // namespaces persisted state (sort, filters, fullscreen)
 *   testId?: string
 *   loading?: boolean
 *   emptyMessage?: string
 *   stickyHeader?: boolean (default true)
 *   density?: "compact"|"comfortable" (default "compact")
 */
export default function DataTable({
    columns,
    rows,
    rowKey,
    onRowClick,
    onCellEdit,
    actions,
    storageKey = "datatable",
    testId = "datatable",
    loading = false,
    emptyMessage = "No data.",
    stickyHeader = true,
    density = "compact",
    toolbarExtra = null,
    freezeFirstColumn = false,
    rowClassName,
}) {
    // Allow `freezeFirstColumn` to be a number (count of left columns) or boolean
    const requestedFreeze = typeof freezeFirstColumn === "number" ? freezeFirstColumn : (freezeFirstColumn ? 1 : 0);

    // Mobile-safe freeze: sticky columns are great on desktop but trap small
    // viewports because the frozen column alone can be wider than the screen.
    // We disable freeze below the `lg` breakpoint (1024px) so users on mobile
    // can scroll horizontally through every column without an invisible column
    // stuck under their thumb.
    const [isWide, setIsWide] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
    );
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(min-width: 1024px)");
        const onChange = (e) => setIsWide(e.matches);
        try { mq.addEventListener("change", onChange); } catch { mq.addListener(onChange); }
        return () => {
            try { mq.removeEventListener("change", onChange); } catch { mq.removeListener(onChange); }
        };
    }, []);
    const freezeCount = isWide ? requestedFreeze : 0;

    // Pre-compute cumulative left offsets so multi-column freeze stacks cleanly
    const leftOffsets = useMemo(() => {
        const out = [];
        let acc = 0;
        for (let i = 0; i < columns.length; i++) {
            out.push(acc);
            if (i < freezeCount) {
                const w = parseInt(String(columns[i].width || 160), 10) || 160;
                acc += w;
            }
        }
        return out;
    }, [columns, freezeCount]);
    const [sort, setSort] = useState(() => readState(storageKey, "sort", { key: null, dir: "asc" }));
    const [globalQ, setGlobalQ] = useState("");
    const [colFilters, setColFilters] = useState(() => readState(storageKey, "filters", {}));
    const [showFilters, setShowFilters] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [editing, setEditing] = useState(null); // { rowKey, colKey, draft }
    const [columnWidths, setColumnWidths] = useState(() => readState(storageKey, "widths", {}));
    const tableRef = useRef(null);
    const clickTimerRef = useRef(null);
    const resizeRef = useRef(null); // { colKey, startX, startWidth }

    // Persist widths
    useEffect(() => writeState(storageKey, "widths", columnWidths), [columnWidths, storageKey]);

    // Cancel any pending single-click navigation when the table unmounts
    useEffect(() => () => {
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    }, []);

    // Column resize: track mouse globally while a handle is dragged.
    useEffect(() => {
        const onMove = (e) => {
            if (!resizeRef.current) return;
            const { colKey, startX, startWidth } = resizeRef.current;
            const next = Math.max(60, Math.round(startWidth + (e.clientX - startX)));
            setColumnWidths((w) => ({ ...w, [colKey]: next }));
        };
        const onUp = () => { resizeRef.current = null; document.body.style.cursor = ""; document.body.style.userSelect = ""; };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }, []);

    const startResize = (e, colKey, currentWidth) => {
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = { colKey, startX: e.clientX, startWidth: currentWidth || 140 };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    };

    const resolvedWidth = (c) => columnWidths[c.key] ?? (typeof c.width === "number" ? c.width : parseInt(c.width || "140", 10));

    // Persist sort + column filters
    useEffect(() => writeState(storageKey, "sort", sort), [sort, storageKey]);
    useEffect(() => writeState(storageKey, "filters", colFilters), [colFilters, storageKey]);

    // Escape closes fullscreen / inline editor
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                if (editing) setEditing(null);
                else if (fullscreen) setFullscreen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [editing, fullscreen]);

    const filtered = useMemo(() => {
        const lcQ = globalQ.trim().toLowerCase();
        const filterEntries = Object.entries(colFilters).filter(([, v]) => v && v.toString().trim().length);
        return rows.filter((r) => {
            // Global search across all string-accessible columns
            if (lcQ) {
                const hay = columns.map((c) => stringify(accessorVal(c, r))).join(" \u0001 ").toLowerCase();
                if (!hay.includes(lcQ)) return false;
            }
            // Per-column filter (case-insensitive contains)
            for (const [k, v] of filterEntries) {
                const col = columns.find((c) => c.key === k);
                if (!col) continue;
                const val = stringify(accessorVal(col, r)).toLowerCase();
                if (!val.includes(v.toString().toLowerCase())) return false;
            }
            return true;
        });
    }, [rows, globalQ, colFilters, columns]);

    const sorted = useMemo(() => {
        if (!sort.key) return filtered;
        const col = columns.find((c) => c.key === sort.key);
        if (!col) return filtered;
        const arr = [...filtered];
        arr.sort((a, b) => compare(accessorVal(col, a), accessorVal(col, b)));
        if (sort.dir === "desc") arr.reverse();
        return arr;
    }, [filtered, sort, columns]);

    const cellPad = density === "compact" ? "px-3 py-2" : "px-4 py-3";
    const headPad = density === "compact" ? "px-3 py-2" : "px-4 py-3";

    const toggleSort = (col) => {
        if (!col.sortable) return;
        if (sort.key !== col.key) setSort({ key: col.key, dir: "asc" });
        else setSort({ key: col.key, dir: sort.dir === "asc" ? "desc" : "asc" });
    };

    const startEdit = (row, col) => {
        if (!col.editable || !onCellEdit) return;
        setEditing({ rowKey: rowKey(row), colKey: col.key, draft: accessorVal(col, row) });
    };

    const commitEdit = async (row, col) => {
        if (!editing) return;
        const newVal = editing.draft;
        const oldVal = accessorVal(col, row);
        setEditing(null);
        if (stringify(newVal) === stringify(oldVal)) return;
        try { await onCellEdit?.({ row, column: col, value: newVal }); } catch { /* parent toasts */ }
    };

    const clearAllFilters = () => {
        setGlobalQ("");
        setColFilters({});
        setSort({ key: null, dir: "asc" });
    };

    const hasActiveFilters = globalQ || Object.values(colFilters).some((v) => v && v.toString().trim());

    return (
        <div
            ref={tableRef}
            data-testid={testId}
            className={fullscreen
                ? "fixed inset-0 z-[60] bg-white flex flex-col"
                : "border border-border bg-white flex flex-col"}
        >
            {/* Toolbar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/40 flex-wrap">
                <div className="relative flex-1 min-w-[180px] max-w-md">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={globalQ}
                        onChange={(e) => setGlobalQ(e.target.value)}
                        placeholder="Search this view…"
                        className="w-full h-8 pl-8 pr-2 border border-border bg-white text-sm focus:outline-none focus:border-ikb"
                        data-testid={`${testId}-search`}
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setShowFilters((v) => !v)}
                    className={`h-8 px-2 text-xs font-mono uppercase tracking-widest border ${showFilters ? "bg-ikb text-white border-ikb" : "bg-white text-foreground/80 border-border hover:border-ikb"}`}
                    data-testid={`${testId}-toggle-filters`}
                ><FilterIcon className="w-3 h-3 inline -mt-px mr-1" /> Filters</button>
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={clearAllFilters}
                        className="h-8 px-2 text-xs font-mono uppercase tracking-widest border border-border bg-white text-rose-700 hover:border-rose-300"
                        data-testid={`${testId}-clear`}
                    ><X className="w-3 h-3 inline -mt-px mr-1" /> Clear</button>
                )}
                <span className="text-[11px] font-mono uppercase tracking-widest text-foreground/70 ml-auto">
                    {sorted.length.toLocaleString()}<span className="opacity-50"> / {rows.length.toLocaleString()} rows</span>
                </span>
                {toolbarExtra}
                <button
                    type="button"
                    onClick={() => setFullscreen((v) => !v)}
                    className="h-8 px-2 text-xs font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb"
                    data-testid={`${testId}-fullscreen`}
                    title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                >
                    {fullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                </button>
            </div>

            {/* Body */}
            <div className="overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead className={stickyHeader ? "sticky top-0 z-10" : ""}>
                        <tr className="bg-secondary border-b-2 border-border">
                            {columns.map((c, colIdx) => {
                                const active = sort.key === c.key;
                                const w = resolvedWidth(c);
                                const isFrozen = colIdx < freezeCount;
                                return (
                                    <th
                                        key={c.key}
                                        style={{ width: w, minWidth: w, maxWidth: w, textAlign: c.align || "left", ...(isFrozen ? { left: leftOffsets[colIdx], position: "sticky" } : {}) }}
                                        className={`${headPad} text-[10.5px] font-mono uppercase tracking-widest text-foreground/70 border-r border-border last:border-r-0 select-none relative ${c.sortable ? "cursor-pointer hover:text-foreground" : ""} ${isFrozen ? `bg-secondary z-20 ${colIdx === freezeCount - 1 ? "border-r-2 border-r-foreground/30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]" : ""}` : ""}`}
                                        onClick={() => toggleSort(c)}
                                        data-testid={`${testId}-th-${c.key}`}
                                    >
                                        <span className="inline-flex items-center gap-1 truncate">
                                            {c.label}
                                            {c.sortable && active && (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                                        </span>
                                        {c.resizable !== false && (
                                            <span
                                                onMouseDown={(e) => startResize(e, c.key, w)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-ikb/40"
                                                data-testid={`${testId}-resize-${c.key}`}
                                                title="Drag to resize"
                                            />
                                        )}
                                    </th>
                                );
                            })}
                            {actions && <th className={`${headPad} text-right text-[10.5px] font-mono uppercase tracking-widest text-foreground/70 sticky right-0 bg-secondary border-l-2 border-border`} style={{ width: 120 }}>Actions</th>}
                        </tr>
                        {showFilters && (
                            <tr className="bg-white border-b border-border">
                                {columns.map((c) => (
                                    <th key={c.key} className="p-1 border-r border-border last:border-r-0">
                                        {c.filterable !== false ? (
                                            <input
                                                type="text"
                                                value={colFilters[c.key] || ""}
                                                onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                                                placeholder="filter…"
                                                className="w-full h-7 px-2 text-xs border border-border bg-white focus:outline-none focus:border-ikb"
                                                data-testid={`${testId}-filter-${c.key}`}
                                            />
                                        ) : null}
                                    </th>
                                ))}
                                {actions && <th className="p-1"></th>}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-10 text-sm text-muted-foreground font-mono">Loading…</td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-10 text-sm text-muted-foreground">{emptyMessage}</td></tr>
                        ) : sorted.map((row) => {
                            const rk = rowKey(row);
                            // Extra per-row class hook (used for e.g. promoted jobs
                            // that need an amber left-border to stand out).
                            const extraRowCls = rowClassName ? (rowClassName(row) || "") : "";
                            return (
                                <tr
                                    key={rk}
                                    className={`border-b border-border hover:bg-secondary transition-colors group ${extraRowCls}`}
                                    data-testid={`${testId}-row-${rk}`}
                                >
                                    {columns.map((c, colIdx) => {
                                        const value = accessorVal(c, row);
                                        const isEditing = editing && editing.rowKey === rk && editing.colKey === c.key;
                                        const isFrozen = colIdx < freezeCount;
                                        // Cell click coordination:
                                        //   - dblclick on an editable cell → start inline edit
                                        //   - single click on a non-editable cell → navigate the row (if onRowClick set)
                                        //   - single click on an editable cell → delayed row nav (cancelled by an
                                        //     incoming dblclick within 250ms so we don't navigate AND edit at the same time)
                                        const handleClick = (e) => {
                                            if (isEditing || !onRowClick || c.noRowClick) return;
                                            if (!c.editable) {
                                                onRowClick(row);
                                                return;
                                            }
                                            // Editable cell: defer the navigation so a possible dblclick can win.
                                            if (clickTimerRef.current) {
                                                clearTimeout(clickTimerRef.current);
                                                clickTimerRef.current = null;
                                            }
                                            const timer = setTimeout(() => {
                                                clickTimerRef.current = null;
                                                onRowClick(row);
                                            }, 230);
                                            clickTimerRef.current = timer;
                                        };
                                        const handleDblClick = (e) => {
                                            if (!c.editable) return;
                                            e.stopPropagation();
                                            if (clickTimerRef.current) {
                                                clearTimeout(clickTimerRef.current);
                                                clickTimerRef.current = null;
                                            }
                                            startEdit(row, c);
                                        };
                                        return (
                                            <td
                                                key={c.key}
                                                style={{ textAlign: c.align || "left", width: resolvedWidth(c), minWidth: resolvedWidth(c), maxWidth: resolvedWidth(c), ...(isFrozen ? { left: leftOffsets[colIdx], position: "sticky" } : {}) }}
                                                className={`${cellPad} border-r border-border last:border-r-0 truncate ${c.editable && !isEditing ? "cursor-text" : (onRowClick ? "cursor-pointer" : "")} ${isEditing ? "p-0" : ""} ${isFrozen ? `bg-white group-hover:bg-secondary z-10 ${colIdx === freezeCount - 1 ? "border-r-2 border-r-foreground/30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]" : ""}` : ""}`}
                                                onClick={handleClick}
                                                onDoubleClick={handleDblClick}
                                                data-testid={`${testId}-cell-${c.key}-${rk}`}
                                                title={c.editable ? "Double-click to edit" : undefined}
                                            >
                                                {isEditing ? (
                                                    c.editor ? (
                                                        c.editor({
                                                            value: editing.draft,
                                                            onChange: (v) => setEditing({ ...editing, draft: v }),
                                                            onCommit: () => commitEdit(row, c),
                                                            onCancel: () => setEditing(null),
                                                        })
                                                    ) : (
                                                        <DefaultInlineEditor
                                                            value={editing.draft}
                                                            onChange={(v) => setEditing({ ...editing, draft: v })}
                                                            onCommit={() => commitEdit(row, c)}
                                                            onCancel={() => setEditing(null)}
                                                        />
                                                    )
                                                ) : (c.render ? c.render(row, value) : defaultRender(value))}
                                            </td>
                                        );
                                    })}
                                    {actions && (
                                        <td
                                            className={`${cellPad} text-right sticky right-0 bg-white group-hover:bg-secondary border-l-2 border-border`}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`${testId}-actions-${rk}`}
                                        >
                                            <div className="inline-flex items-center gap-1 justify-end">{actions(row)}</div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {fullscreen && (
                <div className="px-3 py-1.5 border-t border-border bg-secondary/40 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                    Press <kbd className="px-1 py-0.5 border border-border bg-white">Esc</kbd> to exit fullscreen.
                </div>
            )}
        </div>
    );
}

function DefaultInlineEditor({ value, onChange, onCommit, onCancel }) {
    const ref = useRef(null);
    useEffect(() => { ref.current?.focus(); ref.current?.select?.(); }, []);
    return (
        <div className="flex items-stretch w-full">
            <input
                ref={ref}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onCommit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); onCommit(); }
                    else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
                }}
                className="flex-1 h-9 px-2 text-sm border-2 border-ikb bg-white focus:outline-none"
            />
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onCommit(); }}
                className="px-2 bg-ikb text-white"
                title="Save (Enter)"
            ><Check className="w-3.5 h-3.5" /></button>
        </div>
    );
}

// Helpers

function accessorVal(col, row) {
    return col.accessor ? col.accessor(row) : row?.[col.key];
}

function stringify(v) {
    if (v === null || v === undefined) return "";
    if (Array.isArray(v)) return v.map(stringify).join(", ");
    if (typeof v === "object") {
        try { return JSON.stringify(v); } catch { return ""; }
    }
    return String(v);
}

function defaultRender(v) {
    if (v === null || v === undefined || v === "") return <span className="text-foreground/30">—</span>;
    if (Array.isArray(v)) return v.map(stringify).join(", ");
    return stringify(v);
}

function compare(a, b) {
    if (a === null || a === undefined || a === "") return 1;
    if (b === null || b === undefined || b === "") return -1;
    const an = Number(a); const bn = Number(b);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
    return stringify(a).localeCompare(stringify(b), undefined, { numeric: true });
}

function readState(ns, key, fallback) {
    try {
        const raw = window.localStorage.getItem(`dt:${ns}:${key}`);
        return raw ? JSON.parse(raw) : fallback;
    } catch { return fallback; }
}
function writeState(ns, key, val) {
    try { window.localStorage.setItem(`dt:${ns}:${key}`, JSON.stringify(val)); } catch { /* ignore */ }
}

/** Helper consumers can use to build a Select-style inline editor. */
export function SelectInlineEditor({ value, onChange, onCommit, onCancel, options }) {
    const ref = useRef(null);
    useEffect(() => { ref.current?.focus(); }, []);
    return (
        <select
            ref={ref}
            value={value ?? ""}
            onChange={(e) => { onChange(e.target.value); }}
            onBlur={onCommit}
            onKeyDown={(e) => {
                if (e.key === "Enter") onCommit();
                else if (e.key === "Escape") onCancel();
            }}
            className="w-full h-9 px-2 text-sm border-2 border-ikb bg-white focus:outline-none"
        >
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
    );
}
